import React from 'react';
import { Trans } from '@lingui/react/macro';

import HowBrandWorks from '@rover/components/src/HowBrandWorks';
import { Link } from '@rover/kibble/core';
import LandingHero from '@rover/react-lib/src/pages/landing/components/LandingHero';
import Guarantees from '@rover/react-lib/src/pages/landing/ServiceLanding/components/Guarantees';
import ServiceLandingServices from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceLandingServices';
import ServiceTargetAudience from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceTargetAudience';
import ServiceValueProposition from '@rover/react-lib/src/pages/landing/ServiceLanding/components/ServiceValueProposition';
import Props from '@rover/react-lib/src/pages/landing/ServiceLanding/pages/ServiceLandingPageProps';
import AppStoreCallout from '@rover/react-lib/src/pages/shared/AppStoreCallout/AppStoreCallout';
import CitiesAndNeighborhoods from '@rover/react-lib/src/pages/shared/CitiesAndNeighborhoods/CitiesAndNeighborhoods';
import {
  SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM,
  ServiceLandingEnum,
} from '@rover/types/src/ServiceLanding';

import LandingHelmet from '../../../components/LandingHelmet';
import { getSearchFiltersFromSearchResults } from '../../../utilities';
import ServiceLandingSection from '../../components/ServiceLandingSection';

const LongTermDogBoarding: React.FC<Props> = ({
  countryConfig,
  citiesAndNeighborhoods,
  locale,
  serviceLandingData,
  serviceLinksWithDisplayName,
  searchResults,
  serviceTypes,
}) => {
  const serviceLandingType = ServiceLandingEnum.LONG_TERM_DOG_BOARDING;
  const serviceTypeSlug = SERVICE_TYPE_SLUG_FOR_SERVICE_LANDING_ENUM[serviceLandingType];

  const { h1, h2, seoTitle, seoMetaDescription } = serviceLandingData;

  const initialSearchFilters = getSearchFiltersFromSearchResults(serviceLandingType, searchResults);

  return (
    <>
      <LandingHelmet title={seoTitle} metaDescription={seoMetaDescription} />
      <LandingHero>
        <LandingHero.Content>
          <LandingHero.Display>{h1}</LandingHero.Display>
          <LandingHero.Text>{h2}</LandingHero.Text>
          <LandingHero.SearchForm
            locale={locale}
            serviceTypes={serviceTypes}
            serviceTypeSlug={serviceTypeSlug}
            initialSearchFilters={initialSearchFilters}
          />
          <LandingHero.TrustPilot locale={locale} />
        </LandingHero.Content>
      </LandingHero>

      <ServiceLandingSection>
        <ServiceValueProposition>
          <ServiceValueProposition.Display>
            <Trans>The next generation of dog kennels is here</Trans>
          </ServiceValueProposition.Display>
          <ServiceValueProposition.List>
            <ServiceValueProposition.ListItem>
              <ServiceValueProposition.Subtitle>
                <Trans>Affordable, personalized pet care</Trans>
              </ServiceValueProposition.Subtitle>
              <ServiceValueProposition.Text>
                <Trans>
                  Worried that long-term dog boarding in a sitter’s home will be more expensive than
                  kennels? Find out{' '}
                  <Link href="/dog-sitting-rates/" target="_blank">
                    how much
                  </Link>{' '}
                  dog sitters near you typically cost.
                </Trans>
              </ServiceValueProposition.Text>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <ServiceValueProposition.Subtitle>
                <Trans>Dog sitters you can trust</Trans>
              </ServiceValueProposition.Subtitle>
              <ServiceValueProposition.Text>
                <Trans>
                  Every sitter profile has been individually reviewed and approved by Rover and
                  sitters on Rover have received millions of 5-star reviews.
                </Trans>
              </ServiceValueProposition.Text>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <ServiceValueProposition.Subtitle>
                <Trans>Loving care in your sitter's home</Trans>
              </ServiceValueProposition.Subtitle>
              <ServiceValueProposition.Text>
                <Trans>
                  It's easy to find long-term dog boarding in your city—just search your zip code to
                  see trusted sitters near you.
                </Trans>
              </ServiceValueProposition.Text>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <ServiceValueProposition.Subtitle>
                <Trans>Easy Meet & Greets</Trans>
              </ServiceValueProposition.Subtitle>
              <ServiceValueProposition.Text>
                <Trans>
                  Meet your dog's perfect match before you book. Request a free in-person Meet &
                  Greet, right from your Rover inbox.
                </Trans>
              </ServiceValueProposition.Text>
            </ServiceValueProposition.ListItem>
            <ServiceValueProposition.ListItem>
              <ServiceValueProposition.Subtitle>
                <Trans>Cute photo updates</Trans>
              </ServiceValueProposition.Subtitle>
              <ServiceValueProposition.Text>
                <Trans>
                  Millions of adorable photos have been sent through Rover! Receive a pic of your
                  dog enjoying a round of fetch.
                </Trans>
              </ServiceValueProposition.Text>
            </ServiceValueProposition.ListItem>
          </ServiceValueProposition.List>
        </ServiceValueProposition>
      </ServiceLandingSection>

      <HowBrandWorks>
        <HowBrandWorks.Display>
          <Trans>Browse to book in 3 steps flat</Trans>
        </HowBrandWorks.Display>
        <HowBrandWorks.List>
          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={1}>
              <Trans>Search</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>Browse sitter profiles, read reviews, and get in touch.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>

          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={2}>
              <Trans>Book and Pay</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>
                It’s that easy! All payments are processed through Rover's secured platform.
              </Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>

          <HowBrandWorks.ListItem>
            <HowBrandWorks.ItemHeading step={3}>
              <Trans>Relax</Trans>
            </HowBrandWorks.ItemHeading>
            <HowBrandWorks.ItemContent>
              <Trans>You just made the best choice for your best friend.</Trans>
            </HowBrandWorks.ItemContent>
          </HowBrandWorks.ListItem>
        </HowBrandWorks.List>
      </HowBrandWorks>

      <ServiceLandingSection>
        <Guarantees serviceTypeSlug={serviceTypeSlug} countryConfig={countryConfig} />
      </ServiceLandingSection>

      <ServiceLandingSection>
        <ServiceTargetAudience data-qa-id="service-landing-description">
          <ServiceTargetAudience.Display>
            <Trans>Book long-term dog boarding</Trans>
          </ServiceTargetAudience.Display>
          <ServiceTargetAudience.Content>
            <Trans>
              Founded in 2011, Rover is the nation's largest community of pet sitters and dog
              walkers. Discover your dog’s home away from home with a loving pet sitter in your
              neighborhood.
            </Trans>
          </ServiceTargetAudience.Content>
        </ServiceTargetAudience>
      </ServiceLandingSection>

      <ServiceLandingSection>
        <ServiceLandingServices {...{ serviceLandingType, serviceLinksWithDisplayName }}>
          <ServiceLandingServices.Display>
            <Trans>Rover offers many services to meet your pet care needs, including:</Trans>
          </ServiceLandingServices.Display>
        </ServiceLandingServices>

        <CitiesAndNeighborhoods citiesOrNeighborhoods={citiesAndNeighborhoods}>
          <CitiesAndNeighborhoods.Display>
            <Trans>Thousands of pet sitters in cities and neighborhoods, including:</Trans>
          </CitiesAndNeighborhoods.Display>
        </CitiesAndNeighborhoods>
      </ServiceLandingSection>

      <ServiceLandingSection mb={['0x', '0x']} data-qa-id="service-landing-download-app-callout">
        <AppStoreCallout />
      </ServiceLandingSection>
    </>
  );
};

export default LongTermDogBoarding;
